import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

// Material UI
import {
    Box,
    Tabs,
    Tab,
    Card,
    CardContent,
    Typography,
    Stack,
    Avatar,
    CircularProgress,
    Pagination,
    Divider,
    IconButton,
    TextField,
    InputAdornment,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link } from 'react-router-dom';


const TABS = [
    { key: 'full-access', label: 'Full Access' },
    { key: 'news', label: 'Times' },
    { key: 'request_mentor', label: 'Mentorship' }
];

const UserRow = ({ user }) => {
    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                    src={user.profile_pic}
                    sx={{ width: 48, height: 48, bgcolor: !user.profile_pic ? '#b6d7f5' : 'transparent' }}
                >
                    {!user.profile_pic && (user.name ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'U')}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Link style={{textDecoration: "none"}} to={`/users/${user.user_id}`} target="_blank">
                        <Typography variant="subtitle1" fontWeight={600}>{user.name || 'Unknown'}</Typography>
                    </Link>
                    <Typography variant="body2" color="text.secondary">{user.email || user.phone_number || '-'}</Typography>
                    {user.activityDetails?.action && (
                        <Typography variant="caption" color="text.secondary">{user.activityDetails.action}</Typography>
                    )}
                </Box>

                <Box textAlign="right">
                    <Typography variant="body1" fontWeight={700} >{user.platform.toUpperCase() ?? '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(user.lastActiveAt)}</Typography>
                </Box>
            </Stack>
        </Box>
    );
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PurchaseTriesUsersList = ({ defaultPageSize = 100 }) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [users, setUsers] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [debounceToken, setDebounceToken] = useState(0);

    const currentAction = TABS[tabIndex].key;

    // useRef to keep latest controller across renders so we can cancel previous call
    const controllerRef = useRef(null);

    // Debounce timer ref
    const debounceRef = useRef(null);

    // Fetch function — receives an AbortSignal
    const fetchUsers = useCallback(async (signal, pg = page, pgSize = pageSize, q = query, action = currentAction) => {
        setLoading(true);
        setError(null);

        try {
            const res = await axios.get("https://api.sovrenn.com/user-activity/purchases", {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("sov_user"),
                },
                params: {
                    productinfo: action,
                    page: pg,
                    page_size: pgSize,
                    q: q || undefined,
                },
                signal, // axios supports AbortSignal in modern versions
            });

            if (res.data?.success) {
                setUsers(res.data.data || []);
                setTotalPages(res.data.pagination?.total_pages || 1);
                setTotalUsers(res.data.pagination?.total_documents || 0);
            } else {
                setError(res.data?.message || "Unexpected API response");
                setUsers([]);
            }
        } catch (err) {
            // Ignore canceled requests — they are expected when user changes tab / query / unmounts
            const isCanceled =
                err?.code === "ERR_CANCELED" ||
                (axios.isCancel && axios.isCancel(err)) ||
                err?.name === "CanceledError";
            if (isCanceled) {
                // do nothing
                return;
            }
            // other errors:
            setError(err.response?.data?.message || err.message || "Failed to fetch");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [currentAction, page, pageSize, query]);

    // Effect: runs when tabIndex, page, pageSize or (debounced) query changes
    useEffect(() => {
        // Cancel any running request
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        const controller = new AbortController();
        controllerRef.current = controller;

        // Debounce query changes (300ms). If you only want debounce for query, move debounce to query effect.
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchUsers(controller.signal, page, pageSize, query, currentAction);
        }, 300);

        return () => {
            // cleanup: abort in-flight request and clear timer
            controller.abort();
            clearTimeout(debounceRef.current);
        };
    }, [currentAction, page, pageSize, query, fetchUsers]);

    // If you change tab or query, reset page to 1:
    useEffect(() => {
        setPage(1);
    }, [tabIndex, query]);

    // (optional) cleanup on unmount — ensures no state updates after unmount
    useEffect(() => {
        return () => {
            if (controllerRef.current) controllerRef.current.abort();
            clearTimeout(debounceRef.current);
        };
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebounceToken(prev => prev + 1), 450);
        return () => clearTimeout(t);
    }, [query]);

    const handleTabChange = (_, value) => {
        setTabIndex(value);
        setError(null);
    };

    const handleRefresh = () => setDebounceToken(prev => prev + 1);
    const handlePageChange = (_, value) => setPage(value);

    return (
        <Box sx={{ marginTop: "100px", maxWidth: 1100, mx: 'auto', p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={700}>Users - Tried to Purchase</Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                    
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Card>
                <CardContent sx={{ p: 0 }}>
                    <Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        {TABS.map((t, i) => (
                            <Tab key={t.key} label={`${t.label} (${t.key})`} />
                        ))}
                    </Tabs>

                    <Divider />

                    {loading ? (
                        <Stack alignItems="center" py={6}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 1 }}>Loading users...</Typography>
                        </Stack>
                    ) : error ? (
                        <Box sx={{ p: 3 }}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{ p: 3 }}>
                            <Typography variant="body2" color="text.secondary">No users found for this tab.</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {users.map((u, idx) => (
                                <React.Fragment key={u.user_id || idx}>
                                    <UserRow user={u} />
                                    {idx < users.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}

                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">{totalUsers} users</Typography>

                                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
                            </Stack>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

PurchaseTriesUsersList.propTypes = {
    defaultPageSize: PropTypes.number
};

export default PurchaseTriesUsersList;
